/**
 * @file StrategiesList.jsx
 * @description Manages trading strategies display and selection with user-specific filtering
 */

import React, { useState, useCallback, useEffect, useRef, PureComponent } from "react";
import Widget from "../Widget";
import {
  PieChart,
  Pie,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  ReferenceLine,
  BarChart,
  Bar,
} from "recharts";
import DgButton from "../../../../components/DgButton";
import DgModal from "../../../../components/DgModal";
import TextInput from "../../../../components/forms/TextInput";
import Request from "../../../../utils/fetchUtils";
import Swal from "sweetalert2";
import { useNavigate } from "react-router";
import { formatAmount } from "../../../../utils/formatAmount";
import Loader from "../Loader";
import { COLORS } from "../../../../enums/pieChartColors";
import FilterSelect from "../../../../components/dashboard/FilterSelect";
import { getFromSession } from "../../../../utils/sessionUtils";
import { getCurrentMonthFilter } from "../../../../utils/dateUtils";

class CustomizedLabel extends PureComponent {
  render() {
    const { x, y, stroke, value } = this.props;
    return (
      <text
        x={x}
        y={y}
        dy={-4}
        fill={"white"}
        fontSize={10}
        textAnchor="middle"
      >
        {value}
      </text>
    );
  }
}

const StrategiesList = ({ initialStrategies, initialStrategiesLoading } = {}) => {
  const navigate = useNavigate();
  // Whether a parent (FundManagerDashboard) owns the fetch and feeds us data
  // via props, vs. standalone usage (Strategies.jsx) where we fetch our own.
  // Captured once: which mode we're in never flips mid-lifetime.
  const isPropDriven = useRef(initialStrategies !== undefined).current;
  const [strategies, setStrategies] = useState(initialStrategies || []);
  const [loading, setLoading] = useState(Boolean(initialStrategiesLoading));
  const [name, setName] = useState("");
  const [brokerId, setBrokerId] = useState("TRADIER");
  const [riskEstimate, setRiskEstimate] = useState("");
  const [fee, setFee] = useState(0);
  const [selectedStrat, setselectedStrat] = useState();
  const [newStrategyModal, setNewStrategyModal] = useState(false);
  const [tradeDashModal, setTradeDashModal] = useState(false);
  const [successModal, setSuccesssModal] = useState(false);
  const [updateFeeModal, setupdateFeeModal] = useState(false);
  const [customAllocations, setCustomAllocations] = useState([]);
  const [activeTab, setActiveTab] = useState("strategies");
  
  // Get user info from session
  const sess = JSON.parse(localStorage.getItem("session") || "{}");
  const userId = sess?.userId;
  const userEmail = sess?.email;
  const role = localStorage.getItem("role");
  const userApplications = getFromSession("applications") || [];


  let data = [];

  const formatNumberWithoutDecimals = (value) => {
    if (typeof value !== 'number') return value;
    return Math.round(value).toLocaleString();
  };

  const formatCurrency = (value) => {
    if (typeof value !== 'number') return value;
    
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
    
    return formatted;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '4px',
          fontSize: '12px'
        }}>
          <p>{`Fecha: ${label}`}</p>
          <p style={{ color: '#3357FF' }}>
            {`Equity: $${formatNumberWithoutDecimals(payload[0].value)}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Extraer custom allocations de los datos de estrategias
  const extractCustomAllocations = (strategiesData) => {
    if (!strategiesData || strategiesData.length === 0) return;

    // Buscar la estrategia que contiene preexistingPositions (ahora custom allocations)
    const strategyWithCustomAllocations = strategiesData.find(strategy => 
      strategy.preexistingPositions && strategy.preexistingPositions.length > 0
    );

    if (strategyWithCustomAllocations && strategyWithCustomAllocations.preexistingPositions) {
      let allocations = strategyWithCustomAllocations.preexistingPositions;
      
      // Filtrado por usuario (excepto admin que ve todo)
      if (role !== "admin" && userId) {
        allocations = allocations.filter(allocation => {
          return allocation.userId === userId || 
                 allocation.userEmail === userEmail ||
                 allocation.ownerId === userId;
        });
      }
      
      setCustomAllocations(allocations);
    } else {
      setCustomAllocations([]);
    }
  };

  const renderCustomAllocationsChart = () => {
    if (!customAllocations || customAllocations.length === 0) {
      return (
        <div className="no-data-message">
          <p>No custom allocations data available</p>
        </div>
      );
    }

    const chartData = customAllocations.map(allocation => ({
      symbol: allocation.symbol,
      value: allocation.marketValue,
      quantity: allocation.quantity,
      price: allocation.avgPrice
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <XAxis 
            dataKey="symbol" 
            tick={{ fill: 'white', fontSize: 12 }}
          />
          <YAxis 
            tick={{ fill: 'white', fontSize: 12 }}
            tickFormatter={formatCurrency}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: 'white',
              border: 'none',
              borderRadius: '4px'
            }}
            formatter={(value, name) => [
              formatCurrency(value),
              name === 'value' ? 'Market Value' : name
            ]}
          />
          <Bar 
            dataKey="value" 
            fill="#3357FF"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const handleFetchData = async (queryFilters = "") => {
    if (typeof userId === "string") {
      setLoading(true);
      
      const filters = queryFilters || getCurrentMonthFilter();
      
      const res = await Request(`/trading/strategy?q=&${filters}`);
      if (res.ok) {
        setLoading(false);
        data = res.data;
        setStrategies(data);
        
        // Extraer custom allocations de los datos de estrategias
        extractCustomAllocations(data);
      } else {
        setLoading(false);
      }
    }
  };

  const createStrategy = async (name) => {
    if (name !== "" && brokerId !== "") {
      setLoading(true);
      const res = await Request(
        `/trading/strategy`,
        {
          name,
          brokerId,
          riskEstimate,
        },
        "POST"
      );
      setLoading(false);
      if (res.ok) {
        openSuccessModal();
        handleFetchData();
      }
    } else {
      Swal.fire(
        "Heads up!",
        "Please fill out all fields before continuing",
        "warning"
      );
    }
  };

  const updateStrategyFee = async () => {
    const res = await Request(
      `/trading/strategy/${selectedStrat.strategyId}`,
      {
        feePercentage: fee,
      },
      "PUT"
    );
    if (
      res.Attributes &&
      res.Attributes.feePercentage &&
      res.Attributes.feePercentage.S
    ) {
      const feeValue = res.Attributes.feePercentage.S;
      Swal.fire("Fee updated", "", "success");

      setStrategies((prevStrategies) =>
        prevStrategies.map((strategy) =>
          strategy.strategyId === selectedStrat.strategyId
            ? { ...strategy, feePercentage: feeValue }
            : strategy
        )
      );

      setFee(feeValue);
      setupdateFeeModal(false);
    }
  };

  const removeStrategy = (strategy) => {
    Swal.fire({
      title: "Remove strategy?",
      text: `"${strategy.name}" will be removed from the list.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remove",
    }).then(async ({ isConfirmed }) => {
      if (!isConfirmed) return;
      const res = await Request(
        `/trading/strategy/${strategy.strategyId}`,
        {},
        "DELETE"
      );
      if (res.ok) {
        setStrategies((prev) =>
          prev.filter((s) => s.strategyId !== strategy.strategyId)
        );
        Swal.fire("Strategy removed", "", "success");
      } else {
        Swal.fire(
          "Could not remove strategy",
          typeof res.error === "string" ? res.error : "Please try again",
          "error"
        );
      }
    });
  };

  const fetch = useCallback(() => {
    handleFetchData();
  }, [userId, userEmail, role]);

  // ADFE-143: a parent that already fetched the strategy list (e.g.
  // FundManagerDashboard, to avoid a second identical /trading/strategy
  // request from AllPendingRequests) feeds it to us via props — mirror it
  // into local state instead of issuing our own duplicate fetch. It re-syncs
  // whenever the parent's data changes (e.g. the initial fetch resolving
  // after our first, still-empty render, or a post-action refresh).
  useEffect(() => {
    if (!isPropDriven) return;
    setStrategies(initialStrategies || []);
    setLoading(Boolean(initialStrategiesLoading));
    extractCustomAllocations(initialStrategies || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialStrategies, initialStrategiesLoading]);

  // Standalone usage (e.g. Strategies.jsx): no parent-supplied data, fetch
  // our own list as before.
  useEffect(() => {
    if (isPropDriven) return;
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewStrategyModal = (isOpen) => {
    setNewStrategyModal(isOpen);
  };

  const handleSuccessModal = (isOpen) => {
    setSuccesssModal(isOpen);
  };

  const handleTradeDashModal = (isOpen) => {
    setTradeDashModal(isOpen);
  };

  const openSuccessModal = () => {
    handleNewStrategyModal(false);
    handleSuccessModal(true);
  };

  const mapHistoricalToLineChart = (strategy = { historical: [] }) => {
    const data = (strategy?.historical || [])
      .filter((item) => item?.balance?.balances?.total_equity !== undefined)
      .map(({ SK, balance: { balances: { total_equity } } }) => {
        const [partition, id, date] = SK.split("#");
        return {
          name: date,
          amount: total_equity,
        };
      });

    if (data.length === 0) return { data: [], minEquity: 0, maxEquity: 0 };

    const minEquity = Math.min(...data.map((d) => d.amount));
    const maxEquity = Math.max(...data.map((d) => d.amount));

    return { data, minEquity, maxEquity };
  };

  const mapPieChart = (strategy, checkPositive) => {
    return strategy.positions
      .filter((p) => (checkPositive ? p.gainLoss > 0 : p.gainLoss < 0))
      .map((p) => ({
        name: p.symbol,
        value: parseFloat(Math.abs(p.gainLoss).toFixed(2)),
      }));
  };

  const mapPositions = (strategy) => {
    const positions = strategy.positions || [];
    // Denominator must be unsigned: a short position's negative value
    // would otherwise flip every share's sign (and can zero/invert the
    // total outright when longs and shorts roughly offset).
    const totalAbsMarketValue = positions.reduce(
      (acc, p) => acc + Math.abs(p.value),
      0
    );
    if (totalAbsMarketValue === 0) return [];
    return positions.map((p) => ({
      name: p.symbol,
      value: parseFloat(
        ((Math.abs(p.value) / totalAbsMarketValue) * 100).toFixed(2)
      ),
    }));
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
    value,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
      >
        {`${name}`}
      </text>
    );
  };

  return (
    <Widget name={"strategies"}>
      <FilterSelect onFilter={handleFetchData} />
      {!loading ? (
        <>
          <div className="heading">
            <h1 className="title">Trading Dashboard</h1>
            {role !== "customer" && activeTab === "strategies" && (
              <div
                className="blank-btn"
                onClick={() => {
                  handleNewStrategyModal(true);
                }}
              >
                <img src="/assets/icons/plus.svg" />
                <span>New strategy</span>
              </div>
            )}
          </div>

          {/* Tabs visibles para todos los usuarios */}
          <div className="tabs-container-minimal">
            <div className="tabs-nav-underline">
              <button
                className={`tab-underline ${activeTab === "strategies" ? "active" : ""}`}
                onClick={() => setActiveTab("strategies")}
              >
                Strategies
              </button>
              <button
                className={`tab-underline ${activeTab === "custom-allocations" ? "active" : ""}`}
                onClick={() => setActiveTab("custom-allocations")}
              >
                {role === "admin" || role === "support" ? "All Custom Allocations" : "My Custom Allocations"}
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === "strategies" && (
              <div className="strategies-tab">
                <div className="mt-10">
                  {strategies.length > 0 ? (
                    strategies.map((strategy) => (
                      <div className="overview-strategy " key={strategy.strategyId}>
                        {(role === "admin" || role === "fundManager") && (
                          <div style={{ position: "absolute", top: 15, right: 20 }}>
                            <DgButton
                              category="secondary"
                              size="small"
                              text="Remove"
                              action={() => removeStrategy(strategy)}
                            />
                          </div>
                        )}
                        <div className="col-span-1 pt-5">
                          <div className="strategy-name">{strategy.name}</div>
                          <div className="bull-bigger">
                            <img src="/assets/icons/dashboard/bull-bigger.svg" />
                          </div>
                          <div className="risk-score-badge">
                            Risk Score: {strategy.riskScore}
                          </div>
                          <div className="total-amount small">
                            Total cash: $
                            {formatAmount(
                              strategy?.balance?.body?.balances?.total_cash,
                              2
                            )}
                          </div>
                          <DgButton
                            category="primary"
                            size="medium"
                            text="View strategy"
                            style={{ margin: "auto" }}
                            action={() => {
                              navigate(`/strategies/strategy/${strategy.strategyId}`);
                            }}
                          />
                          <br />
                          {role === "admin" && (
                            <div
                              className="total-amount small"
                              style={{ fontSize: "18px" }}
                            >
                              Fund specific fee: {strategy.feePercentage || "-"} %{" "}
                              <DgButton
                                category="secondary"
                                size="small"
                                text="Edit"
                                style={{ margin: "auto" }}
                                action={() => {
                                  setupdateFeeModal(true);
                                  setselectedStrat(strategy);
                                  if (strategy.feePercentage) {
                                    setFee(strategy.feePercentage);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                        <div className="col-span-1">
                          <div className="performance">AUM:</div>
                          <div className="performance-amount">
                            ${formatAmount(strategy.currentEquity, 2)}
                          </div>
                          <div className="btn gradient">
                            {formatAmount(strategy.timeWeightedPerformance ?? 0, 2)}%
                          </div>
                          <div className="performance-chart px-5 py-2">
                            {(() => {
                              const { data, minEquity, maxEquity } =
                                mapHistoricalToLineChart(strategy);
                              return (
                                <LineChart width={480} height={200} data={data} margin={{ top: 5, right: 80, left: 20, bottom: 5 }}>
                                  <Line
                                    type="monotone"
                                    dataKey="amount"
                                    stroke="#3357FF"
                                    strokeWidth={2}
                                  />
                                  <Tooltip content={<CustomTooltip />} />
                                  <XAxis dataKey="name" height={60} />
                                  <YAxis
                                    type="number"
                                    domain={[minEquity, maxEquity]}
                                    tickFormatter={formatCurrency}
                                    width={80}
                                  />
                                  <ReferenceLine
                                    y={100000}
                                    stroke="white"
                                    strokeDasharray="3 3"
                                  />
                                </LineChart>
                              );
                            })()}
                          </div>
                        </div>
                        <div className="col-span-1 pt-5">
                          <div style={{ width: "100%", height: 250 }}>
                            {(() => {
                              const data = mapPositions(strategy);
                              return (
                                <ResponsiveContainer>
                                  <PieChart>
                                    <Pie
                                      data={data}
                                      stroke="none"
                                      cx={"50%"}
                                      cy={"50%"}
                                      innerRadius={40}
                                      outerRadius={80}
                                      fill="#8884d8"
                                      dataKey="value"
                                      label={renderCustomizedLabel}
                                    >
                                      {data.map((entry, index) => (
                                        <Cell
                                          key={`cell-${index}`}
                                          fill={COLORS[index % COLORS.length]}
                                        />
                                      ))}
                                    </Pie>
                                    <Tooltip />
                                  </PieChart>
                                </ResponsiveContainer>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-strategies-message">
                      <p>No strategies available</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "custom-allocations" && (
              <div className="custom-allocations-tab">
                {customAllocations.length > 0 ? (
                  <>
                    <div className="allocations-header">
                      <h2 className="allocations-title">
                        {role === "admin" || role === "support"  ? "All Custom Allocations" : "My Custom Allocations"}
                      </h2>
                        {(role === "admin" || role === "support") && (
                            <p className="allocations-subtitle">
                              Viewing custom allocations for all users
                            </p>
                          )}
                        </div>
                    <div className="allocations-summary mt-10">
                      <div className="summary-stats">
                        <div className="stat-card">
                          <div className="stat-icon">
                            <img src="/assets/icons/portfolio.svg" alt="Portfolio" />
                          </div>
                          <div className="stat-info">
                            <span className="stat-label">Total Allocations</span>
                            <span className="stat-value">{customAllocations.length}</span>
                          </div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon">
                            <img src="/assets/icons/dollar.svg" alt="Value" />
                          </div>
                          <div className="stat-info">
                            <span className="stat-label">Total Market Value</span>
                            <span className="stat-value">
                              {formatCurrency(
                                customAllocations.reduce((acc, allocation) => acc + allocation.marketValue, 0)
                              )}
                            </span>
                          </div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-icon">
                            <img src="/assets/icons/trending-up.svg" alt="P&L" />
                          </div>
                          <div className="stat-info">
                            <span className="stat-label">Total P&L</span>
                            <span className={`stat-value ${customAllocations.reduce((acc, allocation) => acc + allocation.pnl, 0) >= 0 ? 'positive' : 'negative'}`}>
                              {formatCurrency(
                                customAllocations.reduce((acc, allocation) => acc + allocation.pnl, 0)
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="allocations-chart-container">
                      <h3 className="chart-title">Allocations by Market Value</h3>
                      <div className="allocations-chart">
                        {renderCustomAllocationsChart()}
                      </div>
                    </div>

                    <div className="allocations-table-container">
                      <h3 className="table-title">Detailed Custom Allocations</h3>
                      <div className="allocations-table">
                        <table className="allocations-data-table">
                          <thead>
                            <tr>
                              <th>Symbol</th>
                              {(role === "admin" || role === "support")  && <th>User</th>}
                              <th>Quantity</th>
                              <th>Avg Price</th>
                              <th>Current Price</th>
                              <th>Market Value</th>
                              <th>P&L</th>
                              <th>P&L %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {customAllocations.map((allocation, index) => (
                              <tr key={index}>
                                <td className="symbol-cell">{allocation.symbol}</td>
                                {(role === "admin" || role === "support") && (
                                  <td className="user-cell">
                                    {allocation.userEmail || allocation.userId || 'N/A'}
                                  </td>
                                )}
                                <td>{allocation.quantity}</td>
                                <td>{formatCurrency(allocation.avgPrice)}</td>
                                <td>{formatCurrency(allocation.currentPrice)}</td>
                                <td>{formatCurrency(allocation.marketValue)}</td>
                                <td className={allocation.pnl >= 0 ? 'positive' : 'negative'}>
                                  {formatCurrency(allocation.pnl)}
                                </td>
                                <td className={allocation.pnlPercent >= 0 ? 'positive' : 'negative'}>
                                  {allocation.pnlPercent.toFixed(2)}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="no-data-message">
                    <h3>No Custom Allocations</h3>
                    <p>
                      {(role === "admin" || role === "support")  
                        ? "There are no custom allocations for any users at this time."
                        : "You don't have any custom allocations at this time."
                      }
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Modals */}
          <div className="modal-new-strategy">
            <DgModal
              modalState={newStrategyModal}
              handleModalState={handleNewStrategyModal}
            >
              <h1 className="title">New Strategy</h1>
              <div className="strategy-content">
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Strategy name",
                      name: "strategyName",
                      tooltipBool: false,
                      value: name,
                      action: (e) => {
                        setName(e.target.value);
                      },
                    }}
                  />
                </div>
                <div className="form-field">
                  <TextInput
                    customInput={{
                      label: "Risk score estimate",
                      name: "riskEstimate",
                      value: riskEstimate,
                      action: (e) => {
                        setRiskEstimate(e.target.value);
                      },
                      tooltipBool: true,
                      tooltipText: "Recommended risk score for this strategy",
                    }}
                  />
                </div>
              </div>
              <DgButton
                category="primary"
                size="large"
                text="Add strategy"
                disabled={loading}
                action={() => {
                  createStrategy(name);
                }}
              />
            </DgModal>
          </div>

          <div className="modal-new-strategy success">
            <DgModal
              modalState={successModal}
              handleModalState={handleSuccessModal}
            >
              <div className="strategy-content">
                <img src="/assets/illos/hand.png" />
                <h2>
                  Strategy has been <strong>successfully created</strong>.
                </h2>
              </div>
              <DgButton
                category="primary"
                size="large"
                text="Back to dashboard"
                action={() => {
                  handleSuccessModal(false);
                }}
              />
            </DgModal>
          </div>

          <DgModal
            modalState={updateFeeModal}
            handleModalState={setupdateFeeModal}
          >
            <h1 className="title">Update fee</h1>
            <div className="strategy-content">
              <div className="form-field">
                <TextInput
                  customInput={{
                    label: "Fee",
                    name: "strategyName",
                    tooltipBool: false,
                    type: "number",
                    value: fee,
                    action: (e) => {
                      setFee(e.target.value);
                    },
                  }}
                />
              </div>
            </div>
            <DgButton
              category="primary"
              size="large"
              text="Save fee"
              action={() => {
                updateStrategyFee();
              }}
            />
          </DgModal>
        </>
      ) : (
        <>
          <div className="heading">
            <h1 className="title">Trading Dashboard</h1>
          </div>
          <Loader />
        </>
      )}
    </Widget>
  );
};

export default StrategiesList;
